import React, { useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Grid, Hidden } from "@mui/material";
import Button from "../../../../../../components/atoms/Button/Button"
import TextField from "../../../../../../controls/TextField";
import Dropdown, { KeyValuePair } from "../../../../../../controls/Select";
import { useFormContext, Controller, useFieldArray } from "react-hook-form";
import { ExpandMore } from '@mui/icons-material'
import { useGetApiV1UnderwritingLookupsFieldsQuery } from '../../../../api';
import { DenyModel, DenyRule } from '../../../../api/api-types';
import { preReqListArray } from '../../../../types/model';
import { get } from 'lodash';
import SearchDropdown from '../../../../../../components/organisms/SearchDropdown';
import { ApplicationField, getAllApplicationFields } from '../../../../../../utils/API';

export type DenyInput = {
    denyRules: Pick<DenyModel, "denyRules">["denyRules"]
}

type Props = {
    denyRules: NonNullable<Pick<DenyModel, "denyRules">["denyRules"]>
    preReqs: number[]
    prefix?: string
    nestIndex?: string
}

//'System.Decimal' | 'System.DateTime' | 'System.String'
const typeLookup = [
    { label: 'String', value: 'System.String' },
    { label: 'Date', value: 'System.DateTime' },
    { label: 'Number', value: 'System.Decimal' },
]

const symbolLookup = [
    { label: 'Less Than or Equal', value: 'LTE' },
    { label: 'Less Than', value: 'LT' },
    { label: 'Equal', value: 'E' },
    { label: 'Not Equal', value: 'NE' },
    { label: 'Greater Than', value: 'GT' },
    { label: 'Greater Than or Equal', value: 'GTE' },
    { label: 'Like', value: 'LK' },
]


const logicLookup = [
    { label: 'AND', value: 'AND' },
    { label: 'OR', value: 'OR' }
]

const nullvalues = [
    { label: 'True', value: true },
    { label: 'False', value: false }
]

const DenyForm: React.FC<Props> = ({
    denyRules = [],
    preReqs = [],
    prefix = '',
    nestIndex = '0'
}) => {
    const [denyRuleFields, setDenyRuleFields] = useState<ApplicationField[]>([])
    const { errors, control, register, setValue, formState: { errors: formStateErrors } } = useFormContext<DenyInput>();
    const { isLoading: avFieldsIsLoading, data: avFields } = useGetApiV1UnderwritingLookupsFieldsQuery(undefined);
    const { fields, append, insert, remove } = useFieldArray({
        control,
        name: 'pre' + prefix,

    })
    useEffect(() => {
        /*Use Effect will run once on mount when given an empty array for dependencies.*/
        if (denyRules?.length === 0) {
            denyRules = [{
                "type": "",
                "key": "","compareKey": "", "symbol": "E", "value": "", "operator": "NONE","notnull":"", "denyRules": []
            }]
        }

        async function getRequest() {
            const fields = await getAllApplicationFields();
            setDenyRuleFields(fields);
        }

        getRequest()
        append(denyRules)
    }, []);

    /**
     * Handles deletion of the target rule or rule group
     * @param index Index of the rule to be deleted
     */
    const removeRule = (index: number) => {
        if (index === (fields.length - 1)) {
            setValue(`${prefix}denyRules[${index - 1}].operator`, "NONE")
        }
        remove(index)
    }

    return (
        <Grid container alignContent={'flex-start'} justifyContent={'center'} direction={'row'} spacing={2} >
            {
                fields.map((item, index) => {
                    if ((item.denyRules?.length > 0) || item.nest) {
                        return (
                            <React.Fragment key={item.id}>
                                <Grid item xs={12} sm={12}>
                                    <Accordion sx={{}} defaultExpanded elevation={1}>
                                        <AccordionSummary expandIcon={<ExpandMore />}>Group {index}</AccordionSummary>
                                        <AccordionDetails>
                                            <Controller
                                                render={({ ref, ...rest }) => <input type="hidden" />}
                                                name={`${prefix}denyRules[${index}].key`}
                                                defaultValue={"Application_AcctType"}
                                            />
                                            <Controller
                                                render={({ ref, ...rest }) => <input type="hidden" />}
                                                name={`${prefix}denyRules[${index}].value`}
                                                defaultValue={"NO VALUE"}
                                            />
                                            <Controller
                                                render={({ ref, ...rest }) => <input type="hidden" />}
                                                name={`${prefix}denyRules[${index}].symbol`}
                                                defaultValue={"E"}
                                            />
                                            <Controller
                                                render={({ ref, ...rest }) => <input type="hidden" />}
                                                name={`${prefix}denyRules[${index}].type`}
                                                defaultValue={'System.String'}
                                            />
                                            <Grid container alignContent={'flex-start'} justifyContent={'center'} direction={'row'} spacing={2}>
                                                <Grid item xs={12} sm={10}>

                                                    <Controller
                                                        render={({ ref, ...rest }) => <Dropdown
                                                            {...rest}
                                                            fullWidth
                                                            label={`Logic Operation`}
                                                            options={logicLookup}
                                                            readOnly={((fields.length - 1) === index)}
                                                            error={Boolean(get(errors, `${prefix}denyRules[${index}].operator`))}
                                                            helperText={get(errors, `${prefix}denyRules[${index}].operator.message`) || ' '} />

                                                        }
                                                        name={`${prefix}denyRules[${index}].operator`}
                                                        rules={{ required: ((fields.length - 1) === index) }}
                                                        defaultValue={item.operator ?? 'NONE'} />
                                                </Grid>

                                                <Grid item xs={4} sm={5}>

                                                    <Button fullWidth variant={'contained'} color="error" disabled={(index === 0)}
                                                        onClick={() => { removeRule(index) }}>Delete Group</Button>
                                                </Grid>

                                            </Grid>

                                            {item.denyRules?.length > 0 ?
                                                <DenyForm denyRules={item.denyRules} preReqs={preReqs} prefix={`${prefix}denyRules.${index}.`} nestIndex={nestIndex + item.id + fields.length} />
                                                :
                                                <DenyForm denyRules={[{
                                                    "type": "",
                                                    "key": "","compareKey": "", "symbol": "E", "value": "", "operator": "NONE", "notnull":"","denyRules": []
                                                }]}
                                                    preReqs={preReqs} prefix={`${prefix}denyRules.${index}.`} nestIndex={item.id} />
                                            }
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            </React.Fragment>
                        )
                    }
                    /*------IF NOT NEST------ */
                    else {
                        return (
                            <React.Fragment key={item.id}>
                                <Grid item xs={12} sm={12}>
                                    <Accordion sx={{}} defaultExpanded elevation={1}>
                                        <AccordionSummary expandIcon={<ExpandMore />}>Rule {index}</AccordionSummary>
                                        <AccordionDetails>
                                            <Controller
                                                control={control}
                                                render={({ ref, ...rest }) => <input type="hidden" />}
                                                name={`${prefix}denyRules[${index}].denyRules`}
                                                defaultValue={[]}
                                            />
                                            <Grid container alignContent={'flex-start'} justifyContent={'center'} direction={'row'} spacing={2}>

                                                <Grid item xs={12} sm={10}>

                                                    <Controller
                                                        defaultValue={item.key ?? ""}
                                                        render={({ onBlur, onChange, value, ref, ...rest }) => {
                                                            return (
                                                                <SearchDropdown
                                                                    {...rest}
                                                                    error={Boolean(get(errors, `${prefix}denyRules[${index}].key`))}
                                                                    helperText={get(errors, `${prefix}denyRules[${index}].key.message`) || ' '}
                                                                    items={denyRuleFields?.filter(x => !x.preReqID || preReqListArray.includes(x.preReqID)).map(x => x.name) ?? []}
                                                                    onChange={(_, data) => onChange(data)}
                                                                    placeholder={"Field"}
                                                                    value={value} />)
                                                        }}
                                                        name={`${prefix}denyRules[${index}].key`}
                                                        rules={{ required: 'Required' }}
                                                        label={'Field 1'}
                                                    />
                                                    <Controller
                                                        defaultValue={item.compareKey ?? ""}
                                                        render={({ onBlur, onChange, value, ref, ...rest }) => {
                                                            return (
                                                                <SearchDropdown
                                                                    {...rest}
                                                                    error={Boolean(get(errors, `${prefix}denyRules[${index}].compareKey`))}
                                                                    helperText={get(errors, `${prefix}denyRules[${index}].compareKey.message`) ?? ' '}
                                                                    items={denyRuleFields?.filter(x => !x.preReqID || preReqListArray.includes(x.preReqID)).map(x => x.name) ?? []}
                                                                    onChange={(_, data) => onChange(data)}
                                                                    placeholder={"Comparison Field (Optional)"}
                                                                    value={value} />)
                                                        }}
                                                        name={`${prefix}denyRules[${index}].compareKey`}
                                                    />

                                                </Grid>

                                                <Grid item xs={12} sm={5}>
                                                    <Controller
                                                        render={({ ref, ...rest }) => <Dropdown
                                                            {...rest}
                                                            fullWidth
                                                            label={'Data Type'}
                                                            options={typeLookup}
                                                            error={Boolean(get(errors, `${prefix}denyRules[${index}].type`))}
                                                            helperText={get(errors, `${prefix}denyRules[${index}].type.message`) || ' '} />}
                                                        name={`${prefix}denyRules[${index}].type`}
                                                        rules={{ required: 'Required' }}
                                                        defaultValue={item.type ?? 'System.String'} />

                                                </Grid>

                                                <Grid item xs={12} sm={5}>
                                                    <Controller
                                                        render={({ ref, ...rest }) => <Dropdown fullWidth
                                                            {...rest}
                                                            label={'Symbol'}
                                                            options={symbolLookup}
                                                            error={Boolean(get(errors, `${prefix}denyRules[${index}].symbol`))}
                                                            helperText={get(errors, `${prefix}denyRules[${index}].symbol.message`) || ' '} />}
                                                        name={`${prefix}denyRules[${index}].symbol`}
                                                        rules={{ required: 'Required' }}
                                                        defaultValue={item.symbol || ''} />
                                                </Grid>

                                                <Grid item xs={12} sm={10}>
                                                    <TextField defaultValue={item.value ?? ''}
                                                        name={`${prefix}denyRules[${index}].value`} fullWidth
                                                        label={'Value'}
                                                        inputProps={{ ref: register() }}
                                                        error={Boolean(get(errors, `${prefix}denyRules[${index}].value`))}
                                                        helperText={get(errors, `${prefix}denyRules[${index}].value.message`) || ' '}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={5}>
                                                    <Controller
                                                        render={({ ref, ...rest }) => <Dropdown
                                                            displayEmpty
                                                            {...rest}
                                                            fullWidth
                                                            label={'Logic Operation'}
                                                            options={logicLookup}
                                                            readOnly={((fields.length - 1) === index)}
                                                            error={Boolean(get(errors, `${prefix}denyRules[${index}].operator`))}
                                                            helperText={get(errors, `${prefix}denyRules[${index}].operator.message`) || ' '} />}
                                                        name={`${prefix}denyRules[${index}].operator`}
                                                        rules={{
                                                            validate: (v) => ((v !== "NONE" || ((fields.length - 1) === index)))
                                                        }}
                                                        defaultValue={item.operator ?? 'NONE'} />
                                                </Grid>
                                                <Grid item xs={12} sm={5}>
                                                    <Controller
                                                        render={({ ref, ...rest }) => <Dropdown
                                                            {...rest}
                                                            fullWidth
                                                            label={'Not Null'}
                                                            options={nullvalues as unknown as KeyValuePair[]}
                                                            error={Boolean(get(errors, `${prefix}denyRules[${index}].notnull`))}
                                                            helperText={get(errors, `${prefix}denyRules[${index}].notnull.message`) || ' '} />}
                                                        name={`${prefix}denyRules[${index}].notnull`}
                                                        rules={{
                                                            validate: value =>
                                                                value !== null && value !== undefined ? true : 'Required',
                                                        }}
                                                        defaultValue={item.notNull} />
 
                                                </Grid>
                                                <Grid item xs={12} sm={10} /> {/*Placeholder grid item to ensure the form and buttons below do not mix. */}

                                                <Grid item xs={4}>
                                                    <Button fullWidth color="error" disabled={(index === 0)}
                                                        onClick={() => removeRule(index)}>Remove Rule</Button>
                                                </Grid>

                                                <Grid item xs={4} sm={4}>
                                                    <Button fullWidth variant={'contained'} color={'primary'} onClick={() => insert(index + 1, { nest: false })}>Add New Rule</Button>
                                                </Grid>

                                                <Grid item xs={4} sm={4}>
                                                    <Button fullWidth variant={'contained'} color={'primary'}
                                                        onClick={() => insert(index + 1, { nest: true })}> Add New Rule Group </Button>
                                                </Grid>
                                            </Grid>

                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>


                            </React.Fragment>
                        )
                    }

                })
            }

        </Grid>
    );
}

export default DenyForm;